import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Megaphone, Flame, TrendingUp, Zap, Award } from 'lucide-react';

const ICON_MAP = {
  megaphone: Megaphone,
  flame: Flame,
  trending_up: TrendingUp,
  zap: Zap,
};

const COLOR_MAP = {
  pink: 'bg-pink-100 text-pink-700 border-pink-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
};

const SocialPerformanceCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/social-performance/my-profile')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { social_score, metrics, badges, nudge } = data;

  // Score color
  const scoreColor = social_score >= 70 ? 'text-green-600' : social_score >= 40 ? 'text-amber-600' : 'text-gray-500';
  const barColor = social_score >= 70 ? 'bg-green-500' : social_score >= 40 ? 'bg-amber-500' : 'bg-gray-400';

  return (
    <Card data-testid="social-performance-card">
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-gray-700">Social Performance</CardTitle>
        <span className={`text-lg font-bold ${scoreColor}`}>{social_score}</span>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        {/* Score bar */}
        <div className="w-full bg-gray-100 rounded-full h-1.5">
          <div className={`${barColor} rounded-full h-1.5 transition-all`} style={{width: `${social_score}%`}} />
        </div>

        {/* Badges */}
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {badges.map(b => {
              const Icon = ICON_MAP[b.icon] || Award;
              return (
                <Badge key={b.id} className={`text-[10px] font-semibold ${COLOR_MAP[b.color] || 'bg-gray-100 text-gray-600'}`} data-testid={`social-badge-${b.id}`}>
                  <Icon className="w-3 h-3 mr-0.5" />{b.label}
                </Badge>
              );
            })}
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-1.5 bg-gray-50 rounded">
            <p className="text-sm font-bold text-gray-800">{metrics.posts_7d}</p>
            <p className="text-[9px] text-gray-400">Posts (7d)</p>
          </div>
          <div className="p-1.5 bg-gray-50 rounded">
            <p className="text-sm font-bold text-gray-800">{metrics.posts_30d}</p>
            <p className="text-[9px] text-gray-400">Posts (30d)</p>
          </div>
          <div className="p-1.5 bg-gray-50 rounded">
            <p className="text-sm font-bold text-gray-800">{Math.round(metrics.post_rate * 100)}%</p>
            <p className="text-[9px] text-gray-400">Post Rate</p>
          </div>
        </div>

        {/* Nudge */}
        {nudge && (
          <div className={`p-2 rounded-lg text-xs ${
            nudge.type === 'progress' ? 'bg-amber-50 border border-amber-200 text-amber-700' :
            nudge.type === 'maintain' ? 'bg-green-50 border border-green-200 text-green-700' :
            'bg-indigo-50 border border-indigo-200 text-indigo-700'
          }`} data-testid="social-nudge">
            {nudge.message}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SocialPerformanceCard;
